import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { usePlayer } from "../context/PlayerContext";
import { upsertPlayer } from "../lib/api";
import AudioToggle from "../components/AudioToggle";
import { armAutoStart, playTick } from "../lib/audio";
import { ChevronRight, Trophy } from "lucide-react";

const TitleScreen = () => {
  const { username, email: savedEmail, setUsername, setEmail } = usePlayer();
  const [name, setName] = useState(username || "");
  const [emailInput, setEmailInput] = useState(savedEmail || "");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Arm music autoplay on first user interaction (browser policy)
  useEffect(() => { armAutoStart(); }, []);

  const enter = async () => {
    const trimmed = name.trim();
    const emailTrimmed = emailInput.trim();
    if (!trimmed) return toast.error("Tell us your name, partner.");
    if (trimmed.length > 24) return toast.error("Names round here are 24 characters or fewer.");
    if (emailTrimmed && (!emailTrimmed.includes("@") || !emailTrimmed.includes("."))) {
      return toast.error("That ain't a valid email, partner.");
    }
    playTick();
    setLoading(true);
    try {
      await upsertPlayer(trimmed, emailTrimmed || null);
      setUsername(trimmed);
      if (emailTrimmed) setEmail(emailTrimmed);
      navigate("/lobby");
    } catch {
      toast.error("Saloon's locked. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-yellow-brand relative overflow-hidden flex flex-col">
      {/* top bar */}
      <div className="px-5 sm:px-12 pt-5 flex items-center justify-end relative z-30">
        <div className="flex items-center gap-3">
          <button
            data-testid="title-leaderboard-link"
            onClick={() => navigate("/leaderboard")}
            className="font-elite text-xs sm:text-sm flex items-center gap-1 underline decoration-dotted text-[color:var(--deepest-brown)] hover:text-[color:var(--red)]"
          >
            <Trophy size={14} /> leaderboard
          </button>
          <AudioToggle ambient />
          <button
            data-testid="dashboard-link-top"
            onClick={() => username && navigate("/dashboard")}
            className="font-elite text-xs sm:text-sm underline decoration-dotted text-[color:var(--deepest-brown)] hover:text-[color:var(--red)] disabled:opacity-40"
            disabled={!username}
          >
            {username ? `${username}'s ledger →` : "ledger locked"}
          </button>
        </div>
      </div>

      {/* HERO — lockup with cups behind & around it (lockup on top z-index) */}
      <div className="relative px-2 sm:px-6 pt-2 z-10" style={{ minHeight: "clamp(180px, 36vw, 520px)" }}>
        {/* Beef cup — left, behind lockup */}
        <img
          src="/brand/cup-beef.png"
          alt="Smokehouse BBQ Beef"
          className="absolute idle-anim"
          style={{
            left: "0",
            bottom: 0,
            height: "clamp(160px, 38vw, 760px)",
            width: "auto",
            zIndex: 5,
            filter: "drop-shadow(6px 8px 0 rgba(58,29,4,0.4))",
          }}
          data-testid="title-mascot-beef"
          draggable={false}
        />

        {/* Chicken cup — right, behind lockup */}
        <img
          src="/brand/cup-chicken.png"
          alt="Smokehouse BBQ Chicken"
          className="absolute idle-anim"
          style={{
            right: "0",
            bottom: 0,
            height: "clamp(150px, 36vw, 720px)",
            width: "auto",
            zIndex: 5,
            animationDelay: "0.7s",
            filter: "drop-shadow(6px 8px 0 rgba(58,29,4,0.4))",
          }}
          data-testid="title-mascot-chicken"
          draggable={false}
        />

        {/* Lockup — centered, top layer. Mobile lockup is 50% smaller. */}
        <div className="relative w-full flex justify-center" style={{ zIndex: 10 }}>
          <img
            src="/brand/lockup.png"
            alt="Fantastic Smokehouse Showdown"
            className="block w-full max-w-[300px] sm:max-w-[640px]"
            data-testid="title-heading"
            draggable={false}
          />
        </div>
      </div>

      {/* copy + CTA — clearly separated below the lockup row */}
      <div className="px-5 sm:px-12 pb-8 pt-2 sm:pt-4 relative z-20">
        <div className="max-w-2xl mx-auto text-center">
          <p className="font-elite text-base sm:text-lg text-[color:var(--deepest-brown)] leading-relaxed">
            Howdy, partner. Word's spread across the frontier — there's two new noodle-slingin'
            legends in town. Meet the smokiest, sauciest, darn-right slurpiest wranglers this side
            of the supermarket: Smokehouse BBQ Chicken and Smokehouse BBQ Beef. It's the{" "}
            <strong>Fantastic Smokehouse Showdown</strong> and things are gonna get saucy.
          </p>
          <div className="font-anton text-[color:var(--red)] text-xl sm:text-2xl tracking-wide mt-3">
            SO — WHO'S SIDE ARE YOU ON?
          </div>

          <div className="mt-6 flex flex-col gap-3 max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <Input
                data-testid="username-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && enter()}
                placeholder="What's your name?"
                className="h-14 bg-[color:var(--bone)] border-4 border-[color:var(--deepest-brown)] rounded-none font-elite text-lg placeholder:text-[color:var(--dark-brown)]/60 shadow-[6px_6px_0_0_rgba(58,29,4,0.45)] flex-1"
                maxLength={24}
              />
              <button
                data-testid="enter-saloon-button"
                disabled={loading}
                onClick={enter}
                className="btn-logo flex items-center justify-center w-full sm:w-auto"
                style={{ minWidth: 0 }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", whiteSpace: "nowrap", transform: "skewX(6deg)" }}>
                  {loading ? "Saddlin'..." : "Enter Saloon"}
                  <ChevronRight size={18} />
                </span>
              </button>
            </div>
            <Input
              data-testid="email-input"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enter()}
              placeholder="email (optional — for special updates, giveaways and more)"
              type="email"
              className="h-11 bg-[color:var(--bone)] border-2 border-[color:var(--deepest-brown)] rounded-none font-elite text-sm placeholder:text-[color:var(--dark-brown)]/60 shadow-[4px_4px_0_0_rgba(58,29,4,0.35)]"
              maxLength={120}
            />
            <p
              data-testid="email-consent-microcopy"
              className="font-elite text-[11px] text-[color:var(--dark-brown)] leading-snug -mt-1 px-1"
            >
              By entering your email you agree to be contacted by Fantastic in accordance with our{" "}
              <a
                href="https://www.fantasticsnacks.com.au/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-dotted hover:text-[color:var(--red)]"
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      {/* bottom tagline strip */}
      <div className="border-t-4 border-[color:var(--deepest-brown)] bg-[color:var(--red)] py-3 mt-auto">
        <div className="font-anton text-white text-center text-lg sm:text-2xl tracking-wide">
          THIS CHICKEN'S GOT BEEF · AND THE BEEF'S NO CHICKEN
        </div>
        <div className="font-elite text-[10px] text-white/60 text-center mt-1">
          ambient: "Western Streets" — Kevin MacLeod, CC BY 4.0
        </div>
      </div>
    </div>
  );
};

export default TitleScreen;
